import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../features/auth/AuthProvider.js";
import { ApiClientError } from "../services/api-client.js";
import * as systemApi from "../services/system-api.js";
import * as licensesApi from "../services/licenses-api.js";
import type { License, SystemInfo, SystemModule } from "../types/api.js";

export function DashboardPage(): JSX.Element {
  const { user, token } = useAuth();
  const [info, setInfo] = useState<SystemInfo | null>(null);
  const [modules, setModules] = useState<SystemModule[] | null>(null);
  const [licenses, setLicenses] = useState<License[] | null>(null);
  const [licensesNote, setLicensesNote] = useState<string | null>(null);
  const [loadState, setLoadState] = useState<"loading" | "ok" | "error">("loading");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setLoadState("loading");
      setErrorMsg(null);
      try {
        const [sys, modList] = await Promise.all([systemApi.fetchSystemInfo(), systemApi.fetchSystemModules()]);
        if (cancelled) return;
        setInfo(sys);
        setModules(modList.modules);

        if (token) {
          try {
            const { licenses: list } = await licensesApi.fetchLicenses(token);
            if (!cancelled) {
              setLicenses(list);
              setLicensesNote(null);
            }
          } catch (e) {
            if (!cancelled) {
              setLicenses(null);
              if (e instanceof ApiClientError && e.status === 403) {
                setLicensesNote(
                  "Su rol no tiene permiso para listar licencias vía API. El bloque de módulos licenciados no está disponible.",
                );
              } else if (e instanceof ApiClientError) {
                setLicensesNote(`No se pudieron cargar licencias: ${e.message}`);
              } else {
                setLicensesNote("No se pudieron cargar licencias.");
              }
            }
          }
        }
        setLoadState("ok");
      } catch (e) {
        if (!cancelled) {
          setLoadState("error");
          setErrorMsg(e instanceof ApiClientError ? e.message : "No se pudo cargar el panel.");
        }
      }
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const displayName = user?.email?.split("@")[0] ?? "usuario";

  return (
    <div className="dashboard">
      <header className="page-header">
        <h1>Hola, {displayName}</h1>
        <p className="text-muted">
          Resumen del portal de cliente. Los datos de plataforma provienen de endpoints públicos o autenticados ya
          disponibles en la API.
        </p>
      </header>

      {loadState === "loading" ? <p className="text-muted">Cargando información…</p> : null}
      {loadState === "error" ? <p className="form-error">{errorMsg}</p> : null}

      {loadState === "ok" && info ? (
        <section className="card-grid">
          <article className="card">
            <h2>Su cuenta</h2>
            <ul className="meta-list">
              <li>
                <strong>Correo:</strong> {user?.email}
              </li>
              <li>
                <strong>Rol:</strong> <code>{user?.role}</code>
              </li>
              <li>
                <strong>Tenant (API):</strong>{" "}
                {user?.tenantId ? <code>{user.tenantId}</code> : <span className="text-muted">No asociado</span>}
              </li>
            </ul>
          </article>
          <article className="card">
            <h2>Plataforma</h2>
            <ul className="meta-list">
              <li>
                <strong>Producto:</strong> {info.product}
              </li>
              <li>
                <strong>API:</strong> {info.api} v{info.version}
              </li>
              <li>
                <strong>Fase:</strong> {info.phase}
              </li>
              <li>
                <strong>Persistencia:</strong> {info.persistence}
              </li>
            </ul>
            <p className="text-muted small">{info.authNote}</p>
          </article>
        </section>
      ) : null}

      {licensesNote ? (
        <p className="text-muted banner-inline" role="status">
          {licensesNote}
        </p>
      ) : null}

      {licenses && licenses.length > 0 ? (
        <section className="dashboard-section">
          <h2>Módulos en sus licencias</h2>
          <p className="text-muted small">
            Datos reales de <code>GET /api/v1/licenses</code> (filtrado por rol en el backend).
          </p>
          <ul className="license-chips">
            {[...new Set(licenses.flatMap((l) => l.modules))].sort().map((m) => (
              <li key={m}>
                <code>{m}</code>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {modules ? (
        <section className="dashboard-section">
          <h2>Accesos operativos</h2>
          <p className="text-muted small">
            Estas áreas tendrán funcionalidad completa cuando existan endpoints de negocio. Puede abrir cada tarjeta
            para ver el estado y las dependencias.
          </p>
          <div className="card-grid links-grid">
            <Link className="card card-link" to="/inventory">
              <h3>Inventario</h3>
              <p>Stock, ubicaciones y movimientos (pendiente de API de dominio).</p>
            </Link>
            <Link className="card card-link" to="/orders">
              <h3>Pedidos</h3>
              <p>Flujo de pedidos y cumplimiento (pendiente de API de dominio).</p>
            </Link>
            <Link className="card card-link" to="/traceability">
              <h3>Trazabilidad</h3>
              <p>Cadena logística y trazabilidad (pendiente de API de dominio).</p>
            </Link>
          </div>
        </section>
      ) : null}
    </div>
  );
}
